<template>
  <DefaultCrudView
    @create-new="newJobDefinition"
    :breadcrumbsItems="breadcrumbs"
    :actions="crudActions"
    containerLarge
  >
    <template slot="content">

      <v-data-table
        :headers="headers"
        :items="jobDefinitions"
        class="elevation-1 compact-table"
        :loading="loading"
        :options.sync="options"
        :server-items-length="itemsLength"
        :footer-props="$defaultDataTableFooterProps"
      >
        <template v-slot:[`item.name`]="{ item }">
          <v-icon left>{{ item.icon }}</v-icon>{{ item.title }}
        </template>

        <template v-slot:[`item.tenant_id`]="{ item }">
          {{ getTenantTitle(item.tenant_id) }}
        </template>

        <template v-slot:[`item.pool_id`]="{ item }">
          {{ getPoolTitle(item.pool_id) }}
        </template>

        <template v-slot:[`item.lane_id`]="{ item }">
          {{ getLaneTitle(item.lane_id) }}
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <v-menu offset-y left>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                v-bind="attrs"
                v-on="on"
              >
                <v-icon>mdi-dots-horizontal</v-icon>
              </v-btn>
            </template>

            <v-list dense>
              <v-list-item @click="editJobDefinition(item)">
                <v-list-item-icon>
                  <v-icon color="defaultIconColor">mdi-pencil-outline</v-icon>
                </v-list-item-icon>
                <v-list-item-title>{{ $t('edit') }}</v-list-item-title>
              </v-list-item>

              <v-list-item @click="setJobPoolStatus(item)">
                <v-list-item-icon>
                  <v-icon color="defaultIconColor">mdi-playlist-check</v-icon>
                </v-list-item-icon>
                <v-list-item-title>{{ $t('job_pool_status') }}</v-list-item-title>
              </v-list-item>

              <v-list-item @click="setInitiators(item)">
                <v-list-item-icon>
                  <v-icon color="defaultIconColor">mdi-account-outline</v-icon>
                </v-list-item-icon>
                <v-list-item-title>{{ $t('initiators') }}</v-list-item-title>
              </v-list-item>

              <v-list-item @click="deleteJobDefinition(item)">
                <v-list-item-icon>
                  <v-icon color="defaultIconColor">mdi-delete-outline</v-icon>
                </v-list-item-icon>
                <v-list-item-title>{{ $t('delete') }}</v-list-item-title>
              </v-list-item>
            </v-list>

            <v-divider></v-divider>

            <ContentActionsPopup
              :data="item"
              :filter-type="jobDefinitionNodeType()"
            />
          </v-menu>
        </template>

      </v-data-table>

      <JobDefinitionDialog
        ref="dialog"
        :jobDefinition="selectedJobDefinition"
        :pools="pools"
        :lanes="lanes"
        :aspects="aspects"
        :tenants="tenants"
        :appTenantId="appTenantId"
        @saved="handleSaved"
      />

      <JobDefinitionInitiators ref="initiatorsDialog" />

      <DefaultModal
        ref="deleteDialog"
        :title="$t('confirm_delete_job_definition')"
        deleteModal
        @submit="confirmDelete"
      >
        <template v-slot:content>
          <v-row>
            <v-col cols="12">
              <p>{{ $t('action_cannot_be_undone') }}</p>
              <p v-if="jobDefinitionToDelete">
                <strong>{{ jobDefinitionToDelete.title }}</strong>
              </p>
            </v-col>
          </v-row>
        </template>
      </DefaultModal>

    </template>
  </DefaultCrudView>
</template>

<style scoped>
  .container {
    padding: 0;
  }

  .table-container {
    margin-top: 20px;
  }
</style>

<script>
import { mapActions, mapGetters } from 'vuex'

import NodeTypes from '@/store/content/actions/node-types.js'

import DefaultCrudView from '@/components/DefaultCrudView.vue'
import JobDefinitionDialog from '@/components/job/JobDefinitionDialog.vue'
import JobDefinitionInitiators from '@/components/job/JobDefinitionInitiators.vue'
import DefaultModal from '@/components/DefaultModal.vue'

import AppActions from '@/store/app/actions-types'
import TenantActions from '@/store/core/tenant/actions-types'

import JobDefinitionAPI from '@/services/api/job/job_definition'
import PoolAPI from '@/services/api/job/pool'
import LaneAPI from '@/services/api/job/lane'
import EnablerAPI from '@/services/api/node/enabler'

export default {
  name: "JobDefinition",

  beforeCreate: function () {
    this.$options.components.ContentActionsPopup = require('@/components/content/ContentActionsPopup.vue').default
  },

  components: {
    DefaultCrudView,
    JobDefinitionDialog,
    JobDefinitionInitiators,
    DefaultModal,
  },

  computed: {
    ...mapGetters('app', [
      'getIconForOperationSettingsItem',
      'getUserApplicationById'
    ]),

    breadcrumbs() {
      return [
        {
          text: this.$t('job_settings'),
          prependIcon: 'mdi-cog-outline',
          iconColor: 'primary',
          to: {
            name: "JobSettings",
            params: { appId: this.$route.params.appId }
          },
          disabled: false,
          exact: true,
          ripple: true,
        },
        {
          text: this.$t('job_definitions'),
          prependIcon: 'mdi-clipboard-edit-outline',
        },
      ]
    },

    appInstance() {
      return this.getUserApplicationById(this.$route.params.appId)
    },

    appConfig() {
      if (!this.appInstance || !this.appInstance.config) return {}
      return this.parseApplicationConfig(this.appInstance.config)
    },

    appTenantId() {
      return (
        this.appConfig.tenant_id ||
        this.appConfig.tenantId ||
        (this.appConfig.tenant && (this.appConfig.tenant.id || this.appConfig.tenant.tenant_id)) ||
        null
      )
    }
  },

  data() {
    return {
      loading: true,
      jobDefinitions: [],
      options: {
        sortBy: ['job_definition_id'],
        sortDesc: [true],
      },
      itemsLength: 0,
      crudActions: [
        {
          text: "create_job_definition",
          eventToEmit: "create-new",
        }
      ],
      tenants: [],
      pools: [],
      lanes: [],
      aspects: [],
      selectedJobDefinition: null,
      jobDefinitionToDelete: null,
      headers: [
        { text: this.$t('title'), value: 'name', sortable: true },
        { text: this.$t('tenant'), value: 'tenant_id', sortable: false },
        { text: this.$t('pool'), value: 'pool_id', sortable: false },
        { text: this.$t('lane'), value: 'lane_id', sortable: false },
        { text: this.$t('actions'), value: 'actions', sortable: false }
      ]
    }
  },

  mounted() {
    this.selectedJobDefinition = null

    Promise.all([
      this[TenantActions.GET_ALL_TENANTS](),
      this.loadPools(),
      this.loadLanes(),
      this.loadAllAspects()
    ]).then(([tenantsResponse]) => {
      this.tenants = tenantsResponse.items

      this.ensureContentAppInRoute()
    })
  },

  watch: {
    options: {
      handler () {
        this.reloadData()
      },
      deep: true,
    },
  },

  methods: {
    ...mapActions('core/tenant', [
      TenantActions.GET_ALL_TENANTS
    ]),

    ...mapActions('app', [
      AppActions.OPEN_APP_SUCCESS_MESSAGE
    ]),

    jobDefinitionNodeType() {
      return NodeTypes.NODE_JOB_DEFINITION
    },

    ensureContentAppInRoute() {
      const hasContentParam = !!this.$route.params.contentAppId
      const hasBusinessParam = !!this.$route.params.businessAppId

      // Already carrying both IDs — nothing to do
      if (hasContentParam && hasBusinessParam) return

      const contentAppId = this.appConfig && (this.appConfig.content_app_inst_id || this.appConfig.content_app_id)
      const businessAppId = this.appConfig && (this.appConfig.business_app_inst_id || this.appConfig.business_app_id)

      // Only redirect when at least one app ID is configured
      if (!contentAppId && !businessAppId) return

      // Single redirect carrying both IDs; fall back to 0 (no-op for router guard) when not configured
      this.$router.replace({
        name: 'JobDefinitions',
        params: {
          appId: this.$route.params.appId,
          contentAppId: contentAppId || this.$route.params.contentAppId || 0,
          businessAppId: businessAppId || this.$route.params.businessAppId || 0
        }
      }).catch(() => {})
    },

    loadPools() {
      const options = {}
      if (this.appTenantId) {
        options['filter[tenant]'] = this.appTenantId
      }

      return PoolAPI.getAllPools(options)
        .then((result) => {
          this.pools = result.data.items || []
        })
        .catch((error) => {
          console.error('Error loading pools:', error)
        })
    },

    loadLanes() {
      return LaneAPI.getLanesByUser()
        .then((result) => {
          this.lanes = result.data.items || []
        })
        .catch((error) => {
          console.error('Error loading lanes:', error)
        })
    },

    loadAllAspects() {
      const options = {}
      if (this.appTenantId) {
        options['filter[tenant]'] = this.appTenantId
      }

      return EnablerAPI.getEnablers(options)
        .then((result) => {
          this.aspects = result.data.items || []
        })
        .catch((error) => {
          console.error('Error loading aspects:', error)
        })
    },

    newJobDefinition() {
      this.selectedJobDefinition = null
      this.$refs.dialog.openModal()
    },

    handleSaved(message) {
      this[AppActions.OPEN_APP_SUCCESS_MESSAGE](this.$t(message))
      this.reloadData()
    },

    reloadData() {
      let options = {...this.$route.query}

      if (this.options.sortBy.length > 0) {
        options.order = this.options.sortBy[0]
      }

      if (this.options.sortDesc.length > 0 && this.options.sortDesc[0]) {
        options.direction = 'DESC'
      }

      options.limit = this.options.itemsPerPage
      options.offset = this.options.itemsPerPage * (this.options.page - 1)

      // Filter by tenant from app config when available
      if (this.appTenantId) {
        options['filter[tenant]'] = this.appTenantId
      }

      this.loading = true
      JobDefinitionAPI.getAllJobDefinitions(options)
        .then((result) => {
          this.jobDefinitions = result.data.items || []
          this.itemsLength = result.data.total || 0
          this.loading = false
        })
        .catch(() => this.loading = false)
    },

    editJobDefinition(jobDefinition) {
      this.selectedJobDefinition = jobDefinition
      this.$refs.dialog.openModal()
    },

    deleteJobDefinition(jobDefinition) {
      this.jobDefinitionToDelete = jobDefinition
      this.$refs.deleteDialog.openModal()
    },

    confirmDelete() {
      const jobDefinitionId = this.jobDefinitionToDelete.job_definition_id

      JobDefinitionAPI.deleteJobDefinition(jobDefinitionId)
        .then(() => {
          this[AppActions.OPEN_APP_SUCCESS_MESSAGE](this.$t('job_definition_deleted'))
          this.$refs.deleteDialog.closeModal()
          this.jobDefinitionToDelete = null
          this.reloadData()
        })
        .catch((error) => {
          console.error('Error deleting job definition:', error)
          this.$refs.deleteDialog.setIsSubmitting(false)
        })
    },

    getTenantTitle(id) {
      const tenant = this.tenants.find(f => f.tenant_id == id)
      return tenant ? tenant.title : '—'
    },

    getPoolTitle(id) {
      const pool = this.pools.find(f => f.pool_id == id)
      return pool ? pool.pool_title : '—'
    },

    getLaneTitle(id) {
      const lane = this.lanes.find(f => (f.lane_id || f.id) == id)
      return lane ? lane.lane_title : '—'
    },

    setInitiators(jobDefinition) {
      this.$refs.initiatorsDialog.open(jobDefinition)
    },

    setJobPoolStatus(jobDefinition) {
      // Job-definition responses may expose the primary key as either the
      // domain-specific `job_definition_id` or the generic `id`.
      const jobDefinitionId = jobDefinition.job_definition_id ?? jobDefinition.id

      if (jobDefinitionId === undefined || jobDefinitionId === null) {
        console.error('Unable to open Job Pool Status: job definition ID is missing.', jobDefinition)
        return
      }

      this.$router.push({
        name: 'JobPoolStatus',
        params: {
          appId: this.$route.params.appId,
          jobDefinitionId
        },
        query: {
          jobTitle: jobDefinition.title
        }
      })
    },

    parseApplicationConfig(string) {
      try {
        return JSON.parse(string)
      } catch (e) {
        return {}
      }
    }
  }
}
</script>
